/**
 * ConfirmModal.tsx
 * Modale de confirmation réutilisable pour les actions destructives ou sensibles.
 * Remplace les window.confirm() bruts : titre, message, libellés personnalisables,
 * variante "danger", état de chargement pendant l'action async.
 */
import React, { useState } from 'react';
import { Modal } from '../../../ui/Modal';

export interface ConfirmModalProps {
  isOpen: boolean;
  title: string;
  message: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Style rouge pour les suppressions / actions destructives. */
  danger?: boolean;
  onConfirm: () => void | Promise<void>;
  onClose: () => void;
}

export function ConfirmModal({
  isOpen,
  title,
  message,
  confirmLabel = 'Confirmer',
  cancelLabel = 'Annuler',
  danger = false,
  onConfirm,
  onClose,
}: ConfirmModalProps): React.ReactElement | null {
  const [loading, setLoading] = useState(false);

  const handleConfirm = async () => {
    try {
      setLoading(true);
      await onConfirm();
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <Modal isOpen={isOpen} onClose={loading ? () => {} : onClose} title={title} size="small">
      <div style={{ fontSize: 14, color: 'var(--color-text-secondary, #c9c9d4)', lineHeight: 1.5 }}>
        {message}
      </div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 8,
          marginTop: 24,
        }}
      >
        <button
          type="button"
          onClick={onClose}
          disabled={loading}
          style={{
            padding: '8px 16px',
            borderRadius: 8,
            border: '1px solid var(--color-border, rgba(255,255,255,0.15))',
            background: 'transparent',
            color: 'var(--color-text-primary, #fff)',
            fontSize: 13,
            fontWeight: 600,
            cursor: loading ? 'not-allowed' : 'pointer',
            opacity: loading ? 0.5 : 1,
          }}
        >
          {cancelLabel}
        </button>
        <button
          type="button"
          onClick={handleConfirm}
          disabled={loading}
          style={{
            padding: '8px 16px',
            borderRadius: 8,
            border: 'none',
            background: danger ? 'var(--color-danger, #ef4444)' : 'var(--color-primary, #7C3AED)',
            color: '#fff',
            fontSize: 13,
            fontWeight: 600,
            cursor: loading ? 'not-allowed' : 'pointer',
            opacity: loading ? 0.7 : 1,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
          }}
        >
          {loading && (
            <span
              style={{
                width: 13,
                height: 13,
                border: '2px solid rgba(255,255,255,0.4)',
                borderTopColor: '#fff',
                borderRadius: '50%',
                animation: 'spin 0.8s linear infinite',
                display: 'inline-block',
              }}
            />
          )}
          {confirmLabel}
        </button>
      </div>
    </Modal>
  );
}
